import { X } from 'lucide-react'
import { Button } from '@/components/common'
import { removalRowSummary, restoredSkippedDetails } from './prepackSurfaceModel'
import { useLineupPanel } from './useLineupPanel'

const clock = (value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

/**
 * Recent removals (#1090): the Remove packs actions on this surface in the
 * last hour, so Undo stays reachable after the toast has gone. Each row says
 * what left which surfaces and until when it can be undone; an undone one
 * says so and names what stayed removed. Undo puts packs back when they are still available (they return
 * to the pool, not their old lineup positions).
 *
 * `removals` are the list endpoint's rows, `options` { slug, name } for
 * surface names, `undoingId` the removal an Undo is running for.
 */
export function SurfaceRemovalsPanel({ removals, loading, error, surfaceName, options, canUndo, undoingId, onUndo, onRetry, onClose }) {
  const { ref } = useLineupPanel({ inlineQuery: '(min-width: 1024px)', onClose })
  return (
    <aside ref={ref} tabIndex={-1} aria-label="Recent removals" className="fixed inset-0 z-40 flex flex-col bg-[var(--prepacks-surface)] text-[var(--wf-color-text)] lg:static lg:z-auto lg:w-[400px] lg:shrink-0 lg:border-l lg:border-[var(--prepacks-border)]">
      <header className="flex items-center gap-2 border-b border-[var(--prepacks-border)] px-4 py-3">
        <h2 className="text-sm font-semibold">Recent removals</h2>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" aria-label="Close recent removals" onClick={onClose} className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"><X className="h-4 w-4" /></button>
        </div>
      </header>
      <p className="px-4 pt-3 text-xs text-[var(--prepacks-muted)]">Removed from {surfaceName} in the last hour. Undo puts packs back when they are still available.</p>
      <ul aria-busy={loading || undefined} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {loading ? <li className="px-1 py-6 text-center text-xs text-[var(--prepacks-muted)]">Loading recent removals…</li> : null}
        {!loading && error ? (
          <li className="flex flex-col items-center gap-2 px-1 py-6 text-center text-xs">
            <span role="alert" className="text-[var(--wf-color-danger)]">{error}</span>
            <Button variant="secondary" size="compact" onClick={onRetry}>Retry</Button>
          </li>
        ) : null}
        {!loading && !error && removals.length === 0 ? <li className="px-1 py-6 text-center text-xs text-[var(--prepacks-muted)]">Nothing was removed from {surfaceName} in the last hour.</li> : null}
        {!loading && !error ? removals.map((removal) => {
          const count = removal.packs?.length || 0
          return (
            <li key={removal.id} className="flex items-start gap-3 border-b border-[var(--prepacks-border)] px-1 py-2.5 text-xs last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{removalRowSummary(removal, options)}</p>
                <p className="text-[var(--prepacks-muted)]"><time dateTime={removal.created_at}>{clock(removal.created_at)}</time> · Undo until {clock(removal.expires_at)}</p>
                {removal.undone_at && removal.skipped?.length ? (
                  <p className="text-[var(--wf-color-danger)]">{restoredSkippedDetails(removal.skipped)}</p>
                ) : null}
              </div>
              {removal.undone_at ? (
                <span className="shrink-0 rounded border border-[var(--prepacks-border)] px-2 py-0.5 text-[var(--prepacks-muted)]">Undone</span>
              ) : (
                <Button
                  variant="secondary"
                  size="compact"
                  aria-label={`Undo removal of ${count} pack${count === 1 ? '' : 's'}`}
                  loading={undoingId === removal.id}
                  disabled={!canUndo || Boolean(undoingId)}
                  onClick={() => onUndo(removal.id)}
                >
                  Undo
                </Button>
              )}
            </li>
          )
        }) : null}
      </ul>
    </aside>
  )
}
